import { FC } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty, isNull } from 'lodash';
import {
  createBemBlockBuilder,
  SearchMultiselectDropdown,
} from '@ebsco-ui/ebsco-ui';

import {
  ComponentSpinner,
  MultiselectDropdownItem,
  MultiselectProps,
} from '@app/components';
import { sharedMessages } from '@app/translations';

import './LimiterDropdown.scss';

const cnBem = createBemBlockBuilder(['limiter']);

interface LimiterDropdownProps {
  options: MultiselectDropdownItem[];
  label: string;
  initiallySelectedOptions?: MultiselectDropdownItem[];
  unmappedInitialLimiters?: string[] | null;
  isLoading?: boolean;
  onSelectionChange: Exclude<MultiselectProps['handleSelectChange'], undefined>;
}

export const LimiterDropdown: FC<LimiterDropdownProps> = ({
  options = [],
  label,
  initiallySelectedOptions,
  unmappedInitialLimiters,
  isLoading,
  onSelectionChange,
}) => {
  const { $t } = useIntl();
  const spinnerId = `spinner-${label}`;

  if (!isEmpty(unmappedInitialLimiters) && isEmpty(initiallySelectedOptions)) {
    return (
      <div className="search-multiselect-dropdown" data-auto={spinnerId}>
        <div className="search-multiselect-dropdown__dropdown-label-wrapper">
          <label
            htmlFor={spinnerId}
            className="search-multiselect-dropdown__dropdown-label"
          >
            {label}
          </label>
          <ComponentSpinner id={spinnerId} />
        </div>
      </div>
    );
  }

  return (
    <SearchMultiselectDropdown
      className={cnBem()}
      options={options}
      initialSelectedOptions={initiallySelectedOptions}
      isSearchable={false}
      key={`${isNull(unmappedInitialLimiters)}`} // The component needs to remount when explicitly set to null upon clicking "Clear all"
      isOptionsLoading={isLoading}
      showItemLabelOrValue="LABEL"
      messages={{
        inputBoxPlaceholder: '',
        toggleBtnText: $t(sharedMessages.all),
        clearBtnAriaText: $t(sharedMessages.clear),
        searchBtnAriaText: $t(sharedMessages.search),
        comboboxAriaText: `${label} ${$t(sharedMessages.selectOptions)}`,
        AllText: '',
        loadingText: '',
        selectAllText: '',
        chipsDltBtnAriaText: '',
        clearAllBtnText: '',
        nItemSelectedText: amount =>
          $t(
            {
              id: 'advancedSearch.optionsSelected',
              defaultMessage: '{amount} option(s) selected',
            },
            { amount }
          ),
      }}
      handleSelectChange={onSelectionChange}
    >
      <SearchMultiselectDropdown.Label>{label}</SearchMultiselectDropdown.Label>
      <SearchMultiselectDropdown.Toggle data-auto="multiselect-toggle-button" />
      <SearchMultiselectDropdown.Menu>
        <SearchMultiselectDropdown.Item />
      </SearchMultiselectDropdown.Menu>
    </SearchMultiselectDropdown>
  );
};
